import AcHelper from '@/scripts/helpers/ac-helper';
import resolve from './resolve';
import { ShareOptions, ShareToOptions, ShareToTwitterOptions, ShareToEmailOptions } from './types';

const DEFAULT_DETAILS: Required<ShareOptions> = {
  // Essential
  get 'url'(): string { return resolve.currentUrl(); },
  // Email
  'subject': '',
  'body0': '',
  'body1': '',
  'body-extra': '',
  // Tweet
  'tweet': '',
};

/**
 * Get the context for the sharing
 * @param event usually that come from a click event binded to an element
 * @param params that is used to override the sharing details that get extracted from the binding element
 */
const getContext = (event: Event, params: ShareOptions): { target: HTMLElement; details: Required<ShareOptions> } => {
  const target = resolve.target(event);
  return {
    target,
    details: {
      ...DEFAULT_DETAILS,
      ...resolve.extractElementData(target),
      ...params,
    },
  };
};

// TODO: Similar function are written with some deviation
// Checkout url-location-helper and FacebookLike.vue. Iron out these discrepency and let
// them use a common helper function
/**
 * Encode an key-value pair object into a url component
 * @param {Object} queries
 * @returns {String} encoded uri components
 */
const getEncodedQueries = (queries: Record<string, any>): string => Object
  .entries(queries)
  .map(([key, value]) => `${key}=${encodeURIComponent(value as any)}`)
  .join('&')
;

const shareFB = function shareFB(event: Event, params: ShareToOptions): void {
  const { details } = getContext(event, params);

  // Construct share Link
  const shareLink =
    'https://www.facebook.com/sharer/sharer.php?' +
    getEncodedQueries({
      // Note: https://www.facebook.com/sharer/sharer.php?u= is the facebook share link, u here suppose to be url but change to u to fit the format
      u: details.url,
      pubid: 'ra-54bc7e9f113747fd',
      ct: 1,
    });

  // Open share window
  switch (AcHelper.checkBrowser()) {
    case 'ie7':
    case 'ie8':
    case 'ie9':
      window.open(shareLink);
      break;
    case 'ff':
    case 'ie10':
    case 'chrome':
    default:
      window.open(shareLink, 'facebook-share-dialog', 'width=626,height=436');
      break;
  }
};

const shareTW = function shareTW(event: Event, params: ShareToTwitterOptions): void {
  const { details } = getContext(event, params);

  // Construct share Link
  const shareLink =
    'https://twitter.com/intent/tweet?' +
    getEncodedQueries({
      text: details.tweet,
      url: details.url,
      pubid: 'ra-54bc7e9f113747fd',
      ct: 1,
      via: 'viddsee',
    });

  // Set up tweet window
  const width = 550;
  const height = 420;
  const winHeight = screen.height;
  const winWidth = screen.width;
  const windowOptions = Object.entries({
    scrollbars: 'yes',
    resizable: 'yes',
    toolbar: 'no',
    location: 'yes',
    width,
    height,
    left: Math.round((winWidth / 2) - (width / 2)),
    top: (winHeight > height) ? Math.round((winHeight / 2) - (height / 2)) : 0,
  })
    .map(([key, value]) => key + '=' + value)
    .join(',');

  // Open tweet window
  window.open(shareLink, 'intent', windowOptions);
};

const shareEmail = function shareEmail(event: Event, params: ShareToEmailOptions): void {
  const { details } = getContext(event, params);

  // Default variables
  const body = [
    ...resolve.spread(details.body0),
    ...resolve.spread(details.body1),
  ];
  const bodyText = body[Math.floor(Math.random() * body.length)] || '';

  // Construct share link
  const shareLink =
    'mailto:?' +
    getEncodedQueries({
      subject: details.subject,
      body: [
        ...resolve.spread(bodyText, (str: string) => str + '\n'),
        ...resolve.spread(details['body-extra'], (str: string) => `"${str}"`),
        ...resolve.spread(details.url),
      ].join('\n'),
    });

  // Open share window
  window.location.href = shareLink;
};

const socialSharer = {
  shareFB,
  shareTW,
  shareEmail,
};

export default socialSharer;
